import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { userDetailsByIdQuery } from '../../graphql/queries';
import { LoadingSpinner, ErrorDisplay } from '../LoadingStates';
import BasicInfo from './BasicInfo';
import IncidentsSection from './IncidentsSection';
import LicensesSection from './LicensesSection';
import SuperviseesSection from './SuperviseesSection';
import SupervisorsSection from './SupervisorsSection';
import UpcomingSessionsSection from './UpcomingSessionsSection';
import { Box } from '@mui/material';

interface UserDetailsProps {
  userId: number;
}

export default function UserDetails({ userId }: UserDetailsProps) {
  const afterDate = useMemo(() => new Date(), []);

  const { data, loading, error } = useQuery(userDetailsByIdQuery, {
    variables: { id: userId, afterDate: afterDate.toISOString() },
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  const user = data?.userById;
  const sessions = data?.sessions.result || [];

  if (!user) {
    return <ErrorDisplay error="User not found" />;
  }

  const isOnWaitlist = user.supervisorApplication?.id && !user.passwordSetAt;

  return (
    <>
      <Box mb={2}>
        <BasicInfo user={user} />
      </Box>
      <UpcomingSessionsSection user={user} sessions={sessions} />
      {user.isSupervisor && (
        <>
          <IncidentsSection user={user} />
          <SuperviseesSection user={user} />
          <LicensesSection user={user} />
        </>
      )}
      {user.isSupervisee && <SupervisorsSection user={user} />}
      {isOnWaitlist && <LicensesSection user={user} />}
    </>
  );
}
